import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMeContext } from '../api/me-provider.js';
import { ProfileRoute } from '../routes/profile-route.js';
import { SignInPrompt } from './sign-in-prompt.js';
import { WelcomeSignUp } from './welcome-sign-up.js';
import { WaitNote } from './wait-note.js';

/**
 * The one gate. A route renders only for somebody signed in who has a profile;
 * everyone else is told which of the two they are missing.
 *
 * Not a redirect, in either case. A redirect would rewrite the URL and fight the
 * back button; this renders in place and leaves the address alone, so finishing
 * what was asked for returns them to wherever they were headed.
 *
 * Nothing in the product works without a profile — booking needs a date of birth
 * and a country, and every other surface is about bookings — so there is no
 * useful state where someone browses an app that refuses them everywhere. The
 * header, including Sign out, lives outside this in AppLayout and renders
 * throughout, so this cannot strand anyone.
 *
 * SIGNED OUT used to pass through here, on the grounds that "the public pages
 * are the only thing they can see". There are no public pages, so what they saw
 * was the route with every hook stuck at `loading` — a calendar with a Where
 * selector reading "Loading…" and no hours under it. That is SignInPrompt's job
 * now, and the comment it carries explains the rest.
 *
 * The signed-out branch is trustworthy because AuthProvider does not render the
 * router until initialize() and handleRedirectPromise() have both finished and
 * the active account is set. There is no window in which somebody returning from
 * Entra is momentarily 'signed-out', so this cannot flash at a signed-in person.
 */
export function ProfileGate({ children }: { children: ReactNode }) {
  const { state, reload } = useMeContext();
  const location = useLocation();

  // The development-only claims view stays reachable. It exists to inspect a
  // real token, and a fresh test account — which has no profile by definition —
  // is exactly when that is needed. Absent from production builds entirely.
  if (import.meta.env.DEV && location.pathname === '/token') return <>{children}</>;

  // A family invitation's link goes straight to creating the account (stage C),
  // and so does a second parent's (canvas row D).
  const link = location.pathname === '/welcome' || location.pathname === '/join';
  // A clinic's direct link shows the clinic before anyone signs in (ruled 30 October).
  const clinicLink = /^\/clinics\/[^/]+$/.test(location.pathname);
  if (state.status === 'signed-out' && clinicLink) return <>{children}</>;
  if (state.status === 'signed-out') return link ? <WelcomeSignUp /> : <SignInPrompt />;

  // Briefly, on first paint. Rendering the route and then replacing it with the
  // form a moment later is worse than a line of text.
  if (state.status === 'loading') return <WaitNote>Checking your account…</WaitNote>;

  if (state.status === 'failed') {
    // Deliberately NOT falling through to the route. If /api/me did not answer,
    // whether they have a profile is unknown, and showing the form to someone
    // who already has one invites a submission that can only be refused.
    return (
      <>
        <p className="state-note" data-tone="warn">
          {state.message}
        </p>
        <button type="button" className="primary" onClick={reload}>
          Try again
        </button>
      </>
    );
  }

  // An athlete never reaches here - AppLayout gives them their own frame - and
  // an athlete never has an adult profile to complete.
  if (state.status === 'athlete' || state.status === 'athlete-off') return null;

  // The family invitation's landing (stage C) claims the family the coach
  // started, which is what a profile is then finished on - so it is the one
  // route a signed-in adult reaches without one. A second parent's (/join)
  // is the same: joining is what gives them a profile to finish.
  if (!state.me.profile_complete && !link) return <ProfileRoute />;

  // The liability waiver is mandatory (ruled 31 October): every route leads back
  // to the athlete until it is signed. Here and not on that page, because the
  // tabs and the browser's back button leave a page as well as its chevron.
  // waiverHeld is what tells that page to say why they came back.
  const due = state.me.waiver_due_client_id;
  if (due && !link && location.pathname !== `/account/people/${due}`) {
    return <Navigate to={`/account/people/${due}`} replace state={{ waiverHeld: true }} />;
  }

  return <>{children}</>;
}
